import { ChangeEvent, ComponentPropsWithRef, FocusEvent, useMemo } from 'react';

import { useTemplateItems } from '../../../data/data/templates/use-template-items';
import {
  GetTemplateItemsQueryVariables,
  TemplateItemDataFragment,
  TemplateStatus,
} from '../../../data/types/api/graphql';
import { InputSelect } from '../../../elements/form/input/input-select';

export type InputSelectTemplateFieldProps = Omit<
  ComponentPropsWithRef<typeof InputSelect>,
  'options' | 'value' | 'onChange' | 'onBlur' | 'loading'
> & {
  onChange?: (value: string, e: ChangeEvent<HTMLElement>) => void;
  onBlur?: (value: string, e: FocusEvent<HTMLElement>) => void;
  value: string;
  optionVariables?: Omit<GetTemplateItemsQueryVariables, 'statuses'> & {
    statuses?: (TemplateStatus | 'ALL')[];
  };
  healthProviderId: string | null | undefined;
  healthProfessionalId: string | null | undefined;
};

export function InputSelectTemplateField({
  healthProviderId,
  healthProfessionalId,
  optionVariables,
  onChange,
  onBlur,
  ...props
}: InputSelectTemplateFieldProps) {
  const { data, loading } = useTemplateItems(
    {
      ...(optionVariables ?? {}),
      healthProviderId,
      healthProfessionalId,
      statuses: optionVariables?.statuses ?? [TemplateStatus.Active],
    },
    !healthProfessionalId || healthProfessionalId === ''
  );

  const templateItems: TemplateItemDataFragment[] = useMemo(
    () => data?.items ?? [],
    [data]
  );

  return (
    <InputSelect<string>
      {...props}
      options={templateItems.map(option => ({
        key: option.id ?? '',
        title: option.name ?? '',
      }))}
      value={props.value}
      loading={loading}
      disabled={loading}
      onChange={value => {
        // Create a synthetic event to match the expected signature
        const syntheticEvent = {
          currentTarget: document.getElementById(
            props.name || ''
          ) as HTMLElement,
        } as ChangeEvent<HTMLElement>;

        onChange?.(value, syntheticEvent);
      }}
      onBlur={value => {
        // Create a synthetic event to match the expected signature
        const syntheticEvent = {
          currentTarget: document.getElementById(
            props.name || ''
          ) as HTMLElement,
        } as FocusEvent<HTMLElement>;
        onBlur?.(value, syntheticEvent);
      }}
    />
  );
}
